import React, { FC, useState } from 'react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import styled from 'styled-components';
import Variables from '../../../design-system/variables';
import Colours from '../../../design-system/colours';
import {
  keepNonBreakingSpaces,
  toStoredEditorHtml,
} from '../../../common/utils/services/editor-html-utils';

export interface OvQuillEditorProps {
  value: string;
  handleChange?: (value: string) => void;
}

const modules = {
  clipboard: { matchers: [[Node.TEXT_NODE, keepNonBreakingSpaces]] },
};

const OvQuillEditor: FC<OvQuillEditorProps & ReactQuill.ReactQuillProps> = ({
  value,
  handleChange,
  ...props
}) => {
  const [editorHtml, setEditorHtml] = useState(value);
  const isEditorInSync = toStoredEditorHtml(editorHtml) === value;

  const handleEditorChange = (nextEditorHtml: string) => {
    const nextStoredHtml = toStoredEditorHtml(nextEditorHtml);

    setEditorHtml(nextEditorHtml);

    if (nextStoredHtml !== value) {
      handleChange?.(nextStoredHtml);
    }
  };

  return (
    <StyledReactQuill
      modules={modules}
      {...props}
      value={isEditorInSync ? editorHtml : value}
      onChange={handleEditorChange}
      useSemanticHTML={false}
    />
  );
};

export default OvQuillEditor;

const StyledReactQuill = styled(ReactQuill)`
  .ql-toolbar {
    border: 1px solid ${Colours.OV_BASE};
    border-top-left-radius: ${Variables.borderRadius.SMALL};
    border-top-right-radius: ${Variables.borderRadius.SMALL};
    background-color: ${Colours.WHITE};
  }

  .ql-container {
    border: 1px solid ${Colours.OV_BASE};
    border-bottom-left-radius: ${Variables.borderRadius.SMALL};
    border-bottom-right-radius: ${Variables.borderRadius.SMALL};
    background-color: ${Colours.WHITE};
    min-height: 12.5rem;

    .ql-editor {
      font-family: CentraNo2-Book, CentraNo2, CentraNo2-Medium, -apple-system,
        BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial,
        sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol';
      font-size: 0.875rem;
      line-height: 1.5rem;
      color: ${Colours.OV_BASE};

      &.ql-blank::before {
        color: ${Colours.OV_BASE};
        opacity: 0.42;
        font-style: normal;
        font-size: 0.875rem;
        line-height: 1.5rem;
      }

      p,
      h1,
      h2,
      h3,
      h4,
      h5 {
        margin: 0;
        padding: 0;
      }

      ul {
        list-style-type: disc;
      }

      li {
        list-style-position: outside;
        white-space: pre-line;
      }
    }
  }
`;
